import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { TrendingUp, Zap, Clock, Coins } from "lucide-react";

const AnimatedCounter = ({ value, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const numericValue = parseFloat(value.replace(/[^0-9.]/g, ""));

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const steps = 60;
      const increment = numericValue / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= numericValue) {
          setCount(numericValue);
          clearInterval(timer);
        } else {
          setCount(current);
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, numericValue]);

  return (
    <span ref={ref}>
      {prefix}
      {count.toFixed(count % 1 === 0 ? 0 : 1)}
      {suffix}
    </span>
  );
};

const MarketOpportunitySection = () => {
  const stats = [
    {
      icon: Coins,
      value: "47",
      prefix: "$",
      suffix: "B+",
      label: "Current Market Cap",
    },
    {
      icon: TrendingUp,
      value: "120",
      prefix: "$",
      suffix: "B",
      label: "Projected by 2035",
    },
    {
      icon: Zap,
      value: "12",
      prefix: "$",
      suffix: "B",
      label: "AI Trading Market",
    },
    {
      icon: Clock,
      value: "500",
      suffix: "+",
      label: "New Tokens Daily",
    },
  ];

  return (
    <section
      id="market"
      className="relative py-16 md:py-24 overflow-hidden"
      data-testid="market-opportunity-section"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#8B5CF6]/5 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <span className="inline-block px-3 md:px-4 py-2 rounded-full glass text-xs md:text-sm font-mono text-[#00D4FF] mb-3 md:mb-4">
            MARKET OPPORTUNITY
          </span>
          <h2 className="font-['Unbounded'] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
            The Meme Coin Market Is
            <br />
            <span className="gradient-text">Exploding</span>
          </h2>
        </motion.div>

        {/* Stats grid - Responsive: 2x2 on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-10 md:mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="p-4 md:p-8 rounded-2xl md:rounded-3xl glass ice-hover text-center"
              data-testid={`market-stat-${index}`}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br from-[#00D4FF] to-[#8B5CF6] flex items-center justify-center mx-auto mb-3 md:mb-4">
                <stat.icon size={20} className="text-white md:w-6 md:h-6" />
              </div>
              <div className="font-['Unbounded'] text-2xl sm:text-3xl md:text-4xl font-black text-white mb-1 md:mb-2 neon-blue">
                <AnimatedCounter
                  value={stat.value}
                  prefix={stat.prefix || ""}
                  suffix={stat.suffix || ""}
                />
              </div>
              <div className="text-xs md:text-sm text-[#A1A1AA] font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-3xl mx-auto text-center px-4"
        >
          <p className="text-base md:text-lg lg:text-xl text-[#A1A1AA] leading-relaxed">
            The meme coin market moves too fast for manual trading. New tokens
            launch every minute. Pumps happen in seconds. By the time you hear
            about it, it's too late —{" "}
            <span className="text-white font-semibold">
              unless you have an AI doing it for you.
            </span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default MarketOpportunitySection;
